import { useState } from "react";
import { Navbar } from "@/components/Navbar";
import { Footer } from "@/components/Footer";
import wideLogo from "@/assets/wide-logo.png";

const SUBMIT_EMAIL = "officers@bytesizedpython.org";

// Styles that Tailwind can't express cleanly (custom radio/checkbox marks, print rules).
const formCss = `
.chapter-form input, .chapter-form textarea { font-family: 'DM Sans', system-ui, sans-serif; }
.chapter-form input::placeholder, .chapter-form textarea::placeholder { color: #94A3B8; }
.chapter-form input[type="radio"], .chapter-form input[type="checkbox"] {
  appearance: none; -webkit-appearance: none; margin: 0; flex: none;
  width: 15px; height: 15px; border: 1.5px solid #94A3B8;
  background: #fff; cursor: pointer; position: relative;
  print-color-adjust: exact; -webkit-print-color-adjust: exact;
}
.chapter-form input[type="radio"] { border-radius: 50%; }
.chapter-form input[type="checkbox"] { border-radius: 4px; }
.chapter-form input[type="radio"]:checked, .chapter-form input[type="checkbox"]:checked { border-color: #3776AB; }
.chapter-form input[type="radio"]:checked::after {
  content: ""; position: absolute; inset: 2.5px; border-radius: 50%; background: #3776AB;
}
.chapter-form input[type="checkbox"]:checked::after {
  content: ""; position: absolute; left: 3.5px; top: 0.5px; width: 5px; height: 9px;
  border: solid #3776AB; border-width: 0 2px 2px 0; transform: rotate(40deg);
}
@media print {
  @page { size: letter; margin: 0.6in; }
  body { background: #fff !important; }
  .chapter-form input, .chapter-form textarea { color: #1E293B !important; }
}
`;

// Underline text field (Q1–7, 17–18)
const lineInput =
  "w-full box-border rounded-none border-0 border-b-[1.5px] border-[#CBD5E1] bg-transparent px-0.5 py-1.5 text-sm text-[#1E293B] outline-none focus:border-[#3776AB] focus:ring-0 transition-colors";
// BSP Textarea (Q10, 11, 16)
const boxTextarea =
  "w-full box-border min-h-[150px] resize-y rounded-xl border border-[#E2E8F0] bg-white px-3 py-[0.55rem] text-sm leading-[1.65] text-[#1E293B] outline-none transition-colors focus:border-[#3776AB] focus:shadow-[0_0_0_3px_#DBEAFE]";

const labelText = "text-[13.5px] font-semibold text-[#334155]";
const optionText = "flex cursor-pointer items-center gap-[9px] text-[13.5px] text-[#475569]";

const Num = ({ n }: { n: number }) => <span className="text-[#3776AB]">{n}.</span>;
const Optional = () => <span className="font-medium text-[#94A3B8]">Optional</span>;

const SectionHeader = ({ n, title }: { n: number; title: string }) => (
  <div className="flex items-center gap-3 [break-after:avoid]">
    <span className="text-[10.5px] font-bold uppercase tracking-[0.14em] text-[#3776AB]">Section {n}</span>
    <h2 className="m-0 font-heading text-xl font-bold text-[#1E293B]">{title}</h2>
    <span className="h-px flex-1 bg-[#E2E8F0]" />
  </div>
);

const Radios = ({ name, options, value, onChange }: { name: string; options: string[]; value: string; onChange: (v: string) => void }) => (
  <div className="flex flex-wrap gap-x-[26px] gap-y-2.5">
    {options.map((o) => (
      <label key={o} className={optionText}>
        <input type="radio" name={name} checked={value === o} onChange={() => onChange(o)} /> {o}
      </label>
    ))}
  </div>
);

const Chapter = () => {
  const [f, setF] = useState<Record<string, string>>({});
  const [agree, setAgree] = useState(false);
  const bind = (k: string) => ({
    value: f[k] ?? "",
    onChange: (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => setF((p) => ({ ...p, [k]: e.target.value })),
  });
  const radio = (k: string) => ({ value: f[k] ?? "", onChange: (v: string) => setF((p) => ({ ...p, [k]: v })) });

  return (
    <main className="min-h-screen bg-site print:bg-white">
      <style>{formCss}</style>

      <div className="px-4 pb-20 pt-28 print:p-0">
        <div className="chapter-form mx-auto box-border w-full max-w-[8.5in] bg-white p-6 font-sans shadow-[0_10px_30px_-10px_rgba(15,23,42,0.18)] sm:p-[0.6in] print:max-w-none print:p-0 print:shadow-none">
          <div className="flex items-start justify-between gap-6 border-b-2 border-[#3776AB] pb-[18px]">
            <div className="flex items-center gap-[18px]">
              <img src={wideLogo} alt="Byte-Sized Python" className="block h-[50px] w-[159px] object-contain" />
              <span className="w-px self-stretch bg-[#E2E8F0]" />
              <span className="text-[11px] font-semibold uppercase tracking-[0.14em] text-[#64748B]">Chapter program</span>
            </div>
            <button
              type="button"
              onClick={() => window.print()}
              className="inline-flex h-9 items-center justify-center rounded-xl bg-[#3776AB] px-3 text-sm font-semibold text-white shadow-card transition-colors hover:bg-[#2f679a] print:hidden"
            >
              Save as PDF
            </button>
          </div>

          <h1 className="m-0 mt-6 font-heading text-[34px] font-bold leading-[1.12] tracking-[-0.02em] text-[#1E293B]">Chapter Application</h1>

          <p className="m-0 mt-3 max-w-[52em] text-[14.5px] leading-[1.65] text-[#475569]">
            A Byte-Sized Python chapter is an independent, student-led coding education group in your local area, supported by our
            curriculum, materials, and mentors. Chapters don’t need a host school or organization. Students and adults are both welcome
            to apply. Fill in every question below, then save the completed form as a PDF and email it to us.
          </p>

          <div className="mt-5 flex flex-wrap items-center gap-x-3.5 gap-y-2.5 rounded-2xl border border-[#E2E8F0] bg-[rgba(55,118,171,0.06)] px-[18px] py-3.5 [break-inside:avoid]">
            <span className="text-[10.5px] font-bold uppercase tracking-[0.12em] text-[#3776AB]">Send to</span>
            <span className="font-mono text-sm font-medium text-[#1E293B]">{SUBMIT_EMAIL}</span>
            <span className="text-[13px] text-[#64748B]">Subject line: Chapter Application - [your chapter name]</span>
          </div>

          <div className="mt-[38px] flex flex-col gap-[34px]">
            <section className="flex flex-col gap-5">
              <SectionHeader n={1} title="Applicant information" />
              <div className="grid grid-cols-1 gap-x-7 gap-y-[22px] sm:grid-cols-2 print:grid-cols-2">
                <label className="flex flex-col gap-[7px]"><span className={labelText}><Num n={1} /> Full name</span><input type="text" placeholder="First and last" className={lineInput} {...bind("name")} /></label>
                <label className="flex flex-col gap-[7px]"><span className={labelText}><Num n={2} /> Email address</span><input type="email" placeholder="you@example.com" className={lineInput} {...bind("email")} /></label>
                <label className="flex flex-col gap-[7px]"><span className={labelText}><Num n={3} /> Phone number</span><input type="tel" placeholder="(555) 555-5555" className={lineInput} {...bind("phone")} /></label>
                <label className="flex flex-col gap-[7px]"><span className={labelText}><Num n={4} /> Grade level or age, or role if adult</span><input type="text" placeholder="e.g. 9th grade, or Teacher" className={lineInput} {...bind("gradeOrRole")} /></label>
                <label className="flex flex-col gap-[7px]"><span className={labelText}><Num n={5} /> School or organization <Optional /></span><input type="text" className={lineInput} {...bind("organization")} /></label>
                <label className="flex flex-col gap-[7px]"><span className={labelText}><Num n={6} /> City, state</span><input type="text" className={lineInput} {...bind("cityState")} /></label>
              </div>
            </section>

            <section className="flex flex-col gap-5">
              <SectionHeader n={2} title="Proposed chapter details" />
              <label className="flex max-w-[26em] flex-col gap-[7px]"><span className={labelText}><Num n={7} /> Proposed chapter name</span><input type="text" className={lineInput} {...bind("chapterName")} /></label>
              <div className="flex flex-col gap-[11px] [break-inside:avoid]">
                <span className={labelText}><Num n={8} /> Where will your chapter usually meet?</span>
                <Radios name="q8" options={["Online", "Public library", "Community center", "Mix of online and in person", "Other"]} {...radio("q8")} />
              </div>
              <div className="flex flex-col gap-[11px] [break-inside:avoid]">
                <span className={labelText}><Num n={9} /> Estimated number of interested students</span>
                <Radios name="q9" options={["1–10", "11–25", "26–50", "50+"]} {...radio("q9")} />
              </div>
            </section>

            <section className="flex flex-col gap-5">
              <SectionHeader n={3} title="Motivation and fit" />
              <label className="flex flex-col gap-[9px] [break-inside:avoid]"><span className={labelText}><Num n={10} /> Why do you want to start a Byte-Sized Python chapter in your area?</span><textarea rows={6} className={boxTextarea} {...bind("whyStart")} /></label>
              <label className="flex flex-col gap-[9px] [break-inside:avoid]"><span className={labelText}><Num n={11} /> What need or gap will this chapter address for students in your area?</span><textarea rows={6} className={boxTextarea} {...bind("needOrGap")} /></label>
              <div className="flex flex-col gap-[11px] [break-inside:avoid]">
                <span className={labelText}><Num n={12} /> How did you hear about Byte-Sized Python?</span>
                <Radios name="q12" options={["Friend or peer", "Teacher or counselor", "Social media", "BSP website", "Other"]} {...radio("q12")} />
              </div>
            </section>

            <section className="flex flex-col gap-5">
              <SectionHeader n={4} title="Commitment and logistics" />
              <div className="flex flex-col gap-[11px] [break-inside:avoid]">
                <span className={labelText}><Num n={13} /> Do you have access to computers and internet for members?</span>
                <Radios name="q13" options={["Yes", "No", "Partially"]} {...radio("q13")} />
              </div>
              <div className="flex flex-col gap-[11px] rounded-2xl border border-[#FED33B] bg-[rgba(254,211,59,0.20)] px-[18px] py-4 [break-inside:avoid]">
                <span className={labelText}><Num n={14} /> Do you agree to follow Byte-Sized Python’s chapter guidelines and code of conduct? <span className="font-bold text-[#F75D5D]">Required</span></span>
                <label className="flex cursor-pointer items-center gap-[9px] text-[13.5px] font-medium text-[#334155]">
                  <input type="checkbox" name="q14" checked={agree} onChange={(e) => setAgree(e.target.checked)} /> I agree
                </label>
              </div>
              <div className="flex flex-col gap-[11px] [break-inside:avoid]">
                <span className={labelText}><Num n={15} /> Are you willing to submit brief activity updates each semester?</span>
                <Radios name="q15" options={["Yes", "No"]} {...radio("q15")} />
              </div>
              <label className="flex flex-col gap-[9px] [break-inside:avoid]"><span className={labelText}><Num n={16} /> Anything else you’d like us to know? <Optional /></span><textarea rows={6} className={boxTextarea} {...bind("anythingElse")} /></label>
            </section>

            <section className="flex flex-col gap-5">
              <SectionHeader n={5} title="Confirmation" />
              <div className="grid grid-cols-1 gap-x-7 gap-y-[22px] sm:grid-cols-[minmax(0,2fr)_minmax(0,1fr)] print:grid-cols-[minmax(0,2fr)_minmax(0,1fr)] [break-inside:avoid]">
                <label className="flex flex-col gap-[7px]"><span className={labelText}><Num n={17} /> Signature — typed full name</span><input type="text" className={lineInput} {...bind("signature")} /></label>
                <label className="flex flex-col gap-[7px]"><span className={labelText}><Num n={18} /> Date</span><input type="date" className={lineInput} {...bind("date")} /></label>
              </div>
            </section>
          </div>

          <div className="mt-10 flex flex-col gap-2 border-t border-[#E2E8F0] pt-[18px] [break-inside:avoid]">
            <p className="m-0 text-[13px] leading-[1.6] text-[#475569]">
              <strong className="font-semibold text-[#1E293B]">How to submit.</strong> Print this form to a PDF, then email it to{" "}
              <span className="font-mono">{SUBMIT_EMAIL}</span>.
            </p>
            <p className="m-0 text-xs leading-[1.6] text-[#64748B]">The information you provide is used only to review your application and contact you about it.</p>
          </div>
        </div>
      </div>
    </main>
  );
};

export default Chapter;
